import React from 'react';
import FlowToolbar from '../../FlowToolbar';
import TableToolbar from '../../TableToolbar';
import KanbanToolbar from '../../KanbanToolbar';
import GanttToolbar from '../../GanttToolbar';
import TodoListToolbar from '../../TodoListToolbar';
import NeuralToolbar from '../../NeuralToolbar';
import { BOARD_GO_TO_START } from '../../../BoardView/events';

/**
 * ToolbarRenderer - Renders the appropriate toolbar based on view type
 */
const ToolbarRenderer = ({
  effectiveViewType,
  // Mission search (ALL views)
  onOpenMissionSearch,
  // Flow toolbar props
  flowchartRef,
  isFocusMode,
  hasSelection,
  tasks,
  statuses,
  categories,
  onAddTask,
  onAddDecision,
  onAddWait,
  onFocusSelected,
  onExitFocusMode,
  onFilterFocus,
  // Kanban toolbar props
  onAddColumn,
  // Card display settings (per-view)
  cardDisplaySettingsPerView,
  onCardDisplaySettingsChange,
  // Nested sprints (for actionable calculation)
  nestedSprints = [],
  // Swimlane settings
  swimlaneSetting,
  onSwimlaneSettingChange,
  swimlaneContext,
  // Sort settings (per-view)
  sortSettingsPerView,
  onSortSettingChange,
  // Filter settings (per-view)
  filtersPerView,
  onFiltersChange,
  allTags,
  // WIP limits
  onSetWipLimit,
  // Gantt toolbar props (swimlanes - same as Kanban)
  ganttSwimlane,
  onGanttSwimlaneChange,
  ganttSwimlaneContext,
  // Archive props
  showArchived,
  onToggleArchived,
  archivedCount,
  // Actionable only filter
  showActionableOnly,
  onToggleActionableOnly,
  // Nested campaigns visibility filter
  showNestedCampaigns,
  onShowNestedCampaignsChange,
  // TodoList toolbar props
  todoListViewMode,
  onTodoListViewModeChange,
  todoListGroupBy,
  onTodoListGroupByChange,
  onTodoAddTask,
  // New: Todo swimlane setting (same as Kanban)
  todoSwimlane,
  onTodoSwimlaneChange,
  todoSwimlaneContext,
  // New: Show nested items toggle
  todoShowNestedItems,
  onTodoShowNestedItemsChange,
  // Multi-campaign mode
  isMultiCampaignMode = false,
  onOpenCampaignPicker,
  // HUD visibility settings
  flowViewVisibility = {},
  onFlowViewVisibilityChange,
  // FOKUS mode for Kanban and Table views
  kanbanFokus,
  tableFokus,
  // FOKUS mode for Gantt, Todo and Neural views
  ganttFokus,
  todoFokus,
  neuralFokus,
  // Table column visibility
  tableColumnVisibility,
  onTableColumnVisibilityChange,
  // Todo element visibility
  todoElementVisibility,
  onTodoElementVisibilityChange,
  // Table swimlane settings (same as Kanban)
  tableSwimlane,
  onTableSwimlaneChange,
  tableSwimlaneContext,
  // Custom fields (campaign-level definitions)
  customFields = [],
  onManageCustomFields,
  isTidyToolbarBusy = false,
  onResetCampaignProgress,
  onManageVariables = null,
  neuralViewApiRef,
  boardFokus = null,
  boardSelectedIds = null,
}) => {
  switch (effectiveViewType) {
    case 'flow':
      return (
        <FlowToolbar
          flowchartRef={flowchartRef}
          isTidyToolbarBusy={isTidyToolbarBusy}
          isFocusMode={isFocusMode}
          hasSelection={hasSelection}
          tasks={tasks}
          categories={categories}
          statuses={statuses}
          allTags={allTags}
          onAddTask={onAddTask}
          onAddDecision={onAddDecision}
          onAddWait={onAddWait}
          onFocusSelected={onFocusSelected}
          onExitFocusMode={onExitFocusMode}
          onFilterFocus={onFilterFocus}
          cardDisplaySettings={cardDisplaySettingsPerView?.flow}
          onCardDisplaySettingsChange={(newSettings) => onCardDisplaySettingsChange('flow', newSettings)}
          customFields={customFields}
          onManageCustomFields={onManageCustomFields}
          nestedSprints={nestedSprints}
          isMultiCampaignMode={isMultiCampaignMode}
          onOpenCampaignPicker={onOpenCampaignPicker}
          showNestedCampaigns={showNestedCampaigns}
          onShowNestedCampaignsChange={onShowNestedCampaignsChange}
          flowViewVisibility={flowViewVisibility}
          onFlowViewVisibilityChange={onFlowViewVisibilityChange}
          onSetWipLimit={onSetWipLimit}
          filters={filtersPerView?.flow || {}}
          onFiltersChange={(newFilters) => onFiltersChange('flow', newFilters)}
          showActionableOnly={showActionableOnly}
          onToggleActionableOnly={onToggleActionableOnly}
          onOpenMissionSearch={onOpenMissionSearch}
          onResetCampaignProgress={onResetCampaignProgress}
          onManageVariables={onManageVariables}
        />
      );

    case 'table':
      return (
        <TableToolbar 
          onAddTask={onAddTask}
          onAddDecision={onAddDecision}
          onAddWait={onAddWait}
          showArchived={showArchived}
          onToggleArchived={onToggleArchived}
          archivedCount={archivedCount}
          showActionableOnly={showActionableOnly}
          onToggleActionableOnly={onToggleActionableOnly}
          filters={filtersPerView?.table || {}}
          onFiltersChange={(newFilters) => onFiltersChange('table', newFilters)}
          statuses={statuses}
          categories={categories}
          allTags={allTags}
          tasks={tasks}
          showNestedCampaigns={showNestedCampaigns}
          onShowNestedCampaignsChange={onShowNestedCampaignsChange}
          nestedSprints={nestedSprints}
          sortSetting={sortSettingsPerView?.table || 'manual'}
          onSortSettingChange={(newSort) => onSortSettingChange('table', newSort)}
          onSetWipLimit={onSetWipLimit}
          isFokusMode={tableFokus?.isFokusMode}
          selectedTaskIds={tableFokus?.selectedTaskIds || new Set()}
          onFokusSelected={tableFokus?.fokusSelected}
          onExitFokusMode={tableFokus?.exitFokusMode}
          onFokusByFilter={tableFokus?.fokusByFilter}
          columnVisibility={tableColumnVisibility}
          onColumnVisibilityChange={onTableColumnVisibilityChange}
          customFields={customFields}
          onManageCustomFields={onManageCustomFields}
          swimlaneSetting={tableSwimlane}
          onSwimlaneSettingChange={onTableSwimlaneChange}
          swimlaneContext={tableSwimlaneContext}
          onOpenMissionSearch={onOpenMissionSearch}
          onResetCampaignProgress={onResetCampaignProgress}
          onManageVariables={onManageVariables}
        />
      );

    case 'kanban':
      return (
        <KanbanToolbar 
          onAddTask={onAddTask}
          onAddDecision={onAddDecision}
          onAddWait={onAddWait}
          onAddColumn={onAddColumn}
          cardDisplaySettings={cardDisplaySettingsPerView?.kanban}
          onCardDisplaySettingsChange={(newSettings) => onCardDisplaySettingsChange('kanban', newSettings)}
          customFields={customFields}
          onManageCustomFields={onManageCustomFields}
          swimlaneSetting={swimlaneSetting}
          onSwimlaneSettingChange={onSwimlaneSettingChange}
          swimlaneContext={swimlaneContext}
          sortSetting={sortSettingsPerView?.kanban || 'manual'}
          onSortSettingChange={(newSort) => onSortSettingChange('kanban', newSort)}
          filters={filtersPerView?.kanban || {}}
          onFiltersChange={(newFilters) => onFiltersChange('kanban', newFilters)}
          statuses={statuses}
          categories={categories}
          allTags={allTags}
          tasks={tasks}
          onSetWipLimit={onSetWipLimit}
          showArchived={showArchived}
          onToggleArchived={onToggleArchived}
          archivedCount={archivedCount}
          showActionableOnly={showActionableOnly}
          onToggleActionableOnly={onToggleActionableOnly}
          showNestedCampaigns={showNestedCampaigns}
          onShowNestedCampaignsChange={onShowNestedCampaignsChange}
          nestedSprints={nestedSprints}
          isFokusMode={kanbanFokus?.isFokusMode}
          selectedTaskIds={kanbanFokus?.selectedTaskIds || new Set()}
          onFokusSelected={kanbanFokus?.fokusSelected}
          onExitFokusMode={kanbanFokus?.exitFokusMode}
          onFokusByFilter={kanbanFokus?.fokusByFilter}
          onOpenMissionSearch={onOpenMissionSearch}
          onResetCampaignProgress={onResetCampaignProgress}
          onManageVariables={onManageVariables}
        />
      );

    case 'gantt':
      return (
        <GanttToolbar
          onAddTask={onAddTask}
          onAddDecision={onAddDecision}
          onAddWait={onAddWait}
          swimlaneSetting={ganttSwimlane}
          onSwimlaneSettingChange={onGanttSwimlaneChange}
          swimlaneContext={ganttSwimlaneContext}
          sortSetting={sortSettingsPerView?.gantt || 'manual'}
          onSortSettingChange={(newSort) => onSortSettingChange('gantt', newSort)}
          showActionableOnly={showActionableOnly}
          onToggleActionableOnly={onToggleActionableOnly}
          filters={filtersPerView?.gantt || {}}
          onFiltersChange={(newFilters) => onFiltersChange('gantt', newFilters)}
          statuses={statuses}
          categories={categories}
          allTags={allTags}
          tasks={tasks}
          showNestedCampaigns={showNestedCampaigns}
          onShowNestedCampaignsChange={onShowNestedCampaignsChange}
          nestedSprints={nestedSprints}
          onSetWipLimit={onSetWipLimit}
          showArchived={showArchived}
          onToggleArchived={onToggleArchived}
          archivedCount={archivedCount}
          isFokusMode={ganttFokus?.isFokusMode}
          selectedTaskIds={ganttFokus?.selectedTaskIds || new Set()}
          onFokusSelected={ganttFokus?.fokusSelected}
          onExitFokusMode={ganttFokus?.exitFokusMode}
          onFokusByFilter={ganttFokus?.fokusByFilter}
          onOpenMissionSearch={onOpenMissionSearch}
          customFields={customFields}
          onResetCampaignProgress={onResetCampaignProgress}
          onManageVariables={onManageVariables}
        />
      );

    case 'neural':
      return (
        <NeuralToolbar
          onAddTask={onAddTask}
          onAddDecision={onAddDecision}
          onAddWait={onAddWait}
          onOpenMissionSearch={onOpenMissionSearch}
          isMultiCampaignMode={isMultiCampaignMode}
          onOpenCampaignPicker={onOpenCampaignPicker}
          cardDisplaySettings={cardDisplaySettingsPerView?.neural}
          onCardDisplaySettingsChange={(newSettings) => onCardDisplaySettingsChange('neural', newSettings)}
          customFields={customFields}
          onManageCustomFields={onManageCustomFields}
          filters={filtersPerView?.neural || {}}
          onFiltersChange={(newFilters) => onFiltersChange('neural', newFilters)}
          statuses={statuses}
          categories={categories}
          allTags={allTags}
          tasks={tasks}
          showNestedCampaigns={showNestedCampaigns}
          onShowNestedCampaignsChange={onShowNestedCampaignsChange}
          nestedSprints={nestedSprints}
          showActionableOnly={showActionableOnly}
          onToggleActionableOnly={onToggleActionableOnly}
          isFokusMode={neuralFokus?.isFokusMode}
          selectedTaskIds={neuralFokus?.selectedTaskIds || new Set()}
          onFokusSelected={neuralFokus?.fokusSelected}
          onExitFokusMode={neuralFokus?.exitFokusMode}
          onFokusByFilter={neuralFokus?.fokusByFilter}
          onResetCampaignProgress={onResetCampaignProgress}
          onManageVariables={onManageVariables}
          neuralViewApiRef={neuralViewApiRef}
        />
      );

    case 'todolist':
      return (
        <TodoListToolbar
          onAddTask={onTodoAddTask}
          onAddDecision={onAddDecision}
          onAddWait={onAddWait}
          // Swimlane setting (same as Kanban)
          swimlaneSetting={todoSwimlane}
          onSwimlaneSettingChange={onTodoSwimlaneChange}
          swimlaneContext={todoSwimlaneContext}
          // Show nested items toggle
          showNestedItems={todoShowNestedItems}
          onShowNestedItemsChange={onTodoShowNestedItemsChange}
          sortBy={sortSettingsPerView?.todolist || 'manual'}
          onSortChange={(newSort) => onSortSettingChange('todolist', newSort)}
          filters={filtersPerView?.todolist || {}}
          onFiltersChange={(newFilters) => onFiltersChange('todolist', newFilters)}
          showNestedCampaigns={showNestedCampaigns}
          onShowNestedCampaignsChange={onShowNestedCampaignsChange}
          showActionableOnly={showActionableOnly}
          onToggleActionableOnly={onToggleActionableOnly}
          onSetWipLimit={onSetWipLimit}
          elementVisibility={todoElementVisibility}
          onElementVisibilityChange={onTodoElementVisibilityChange}
          customFields={customFields}
          onManageCustomFields={onManageCustomFields}
          showArchived={showArchived}
          onToggleArchived={onToggleArchived}
          archivedCount={archivedCount}
          tasks={tasks}
          statuses={statuses}
          categories={categories}
          allTags={allTags}
          nestedSprints={nestedSprints}
          isFokusMode={todoFokus?.isFokusMode}
          selectedTaskIds={todoFokus?.selectedTaskIds || new Set()}
          onFokusSelected={todoFokus?.fokusSelected}
          onExitFokusMode={todoFokus?.exitFokusMode}
          onFokusByFilter={todoFokus?.fokusByFilter}
          onOpenMissionSearch={onOpenMissionSearch}
          onResetCampaignProgress={onResetCampaignProgress}
          onManageVariables={onManageVariables}
        />
      );

    case 'board':
      return (
        <FlowToolbar
          onGoToStart={() => window.dispatchEvent(new CustomEvent(BOARD_GO_TO_START))}
          isFocusMode={!!boardFokus?.isFokusMode}
          hasSelection={(boardSelectedIds?.size || 0) > 0}
          selectedCount={boardSelectedIds?.size || 0}
          tasks={tasks}
          categories={categories}
          statuses={statuses}
          allTags={allTags}
          onAddTask={onAddTask}
          onAddDecision={onAddDecision}
          onAddWait={onAddWait}
          onFocusSelected={boardFokus?.fokusSelected}
          onExitFocusMode={boardFokus?.exitFokusMode}
          onFilterFocus={boardFokus?.fokusByFilter}
          cardDisplaySettings={cardDisplaySettingsPerView?.flow}
          onCardDisplaySettingsChange={(newSettings) => onCardDisplaySettingsChange('flow', newSettings)}
          customFields={customFields}
          onManageCustomFields={onManageCustomFields}
          nestedSprints={nestedSprints}
          isMultiCampaignMode={isMultiCampaignMode}
          onOpenCampaignPicker={onOpenCampaignPicker}
          showNestedCampaigns={showNestedCampaigns}
          onShowNestedCampaignsChange={onShowNestedCampaignsChange}
          flowViewVisibility={flowViewVisibility}
          onFlowViewVisibilityChange={onFlowViewVisibilityChange}
          onSetWipLimit={onSetWipLimit}
          filters={filtersPerView?.board || {}}
          onFiltersChange={(newFilters) => onFiltersChange('board', newFilters)}
          showActionableOnly={showActionableOnly}
          onToggleActionableOnly={onToggleActionableOnly}
          onOpenMissionSearch={onOpenMissionSearch}
          onResetCampaignProgress={onResetCampaignProgress}
          onManageVariables={onManageVariables}
        />
      );

    default:
      return null;
  }
};

export default ToolbarRenderer;
